import React, { useEffect, useState } from 'react'
import { Typography, Row, Col, Card, Statistic, Table, Tag, Spin } from 'antd'
import { DollarOutlined, CheckCircleOutlined, WarningOutlined, TeamOutlined } from '@ant-design/icons'
import { dashboardAPI, paymentsAPI, attendanceAPI, clientsAPI } from '../services/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts'
import dayjs from 'dayjs'

const { Title, Text } = Typography

const monthShort = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

function Reportes() {
  const [stats, setStats] = useState(null)
  const [income, setIncome] = useState([])
  const [classAttendance, setClassAttendance] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      dashboardAPI.getStats(),
      paymentsAPI.getAll(),
      attendanceAPI.getAll(),
      clientsAPI.getAll()
    ]).then(([s, p, a, c]) => {
      setStats(s.data)

      const now = dayjs()
      const incomeMap = {}
      for (let i = 5; i >= 0; i--) {
        const m = now.subtract(i, 'month')
        const key = m.format('YYYY-MM')
        incomeMap[key] = { month: `${monthShort[m.month()]} ${String(m.year()).slice(2)}`, total: 0 }
      }
      ;(p.data || []).forEach(pay => {
        const key = dayjs(pay.paymentDate).format('YYYY-MM')
        if (incomeMap[key]) incomeMap[key].total += parseFloat(pay.amount) || 0
      })
      setIncome(Object.values(incomeMap))

      const attMap = {}
      ;(a.data || []).forEach(att => {
        const name = att.Class?.name || 'Sin turno'
        attMap[name] = (attMap[name] || 0) + 1
      })
      setClassAttendance(Object.entries(attMap).map(([name, count]) => ({ name, count })).sort((x, y) => y.count - x.count))

      setClients(c.data)
    }).finally(() => setLoading(false))
  }, [])

  if (loading) {
    return <Card style={{ borderRadius: 12, textAlign: 'center', padding: 40 }}><Spin size="large" /></Card>
  }

  const planCounts = {}
  clients.forEach(cl => {
    const plan = cl.planName || 'Sin plan'
    planCounts[plan] = (planCounts[plan] || 0) + 1
  })
  const planData = Object.entries(planCounts).map(([name, count]) => ({ name, count })).sort((x, y) => y.count - x.count)

  const COLORS = ['#ffffff', '#888888', '#666666', '#aaaaaa', '#555555']

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Title level={4} style={{ margin: 0, marginBottom: 12 }}>Reportes</Title>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 12 }} style={{ borderRadius: 12 }}>
            <Statistic title="Alumnas" value={stats?.totalClients || 0} prefix={<TeamOutlined />} valueStyle={{ fontSize: 20 }} />
            <Text style={{ fontSize: 11, color: '#52c41a' }}>{stats?.activeClients || 0} activas</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 12 }} style={{ borderRadius: 12 }}>
            <Statistic title="Cuotas vencidas" value={stats?.expiredClients || 0} prefix={<WarningOutlined />} valueStyle={{ fontSize: 20, color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 12 }} style={{ borderRadius: 12 }}>
            <Statistic title="Ingresos del mes" value={stats?.monthlyIncome || 0} prefix={<DollarOutlined />} suffix="$" valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bodyStyle={{ padding: 12 }} style={{ borderRadius: 12 }}>
            <Statistic title="Asistencias hoy" value={stats?.todayAttendance || 0} prefix={<CheckCircleOutlined />} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={14}>
          <Card size="small" style={{ borderRadius: 12, marginBottom: 12 }} title="Ingresos últimos 6 meses">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={income} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                <XAxis dataKey="month" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip contentStyle={{ background: '#222', border: '1px solid #444', borderRadius: 8 }} formatter={(v) => [`$${v}`, 'Ingresos']} />
                <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                  {income.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card size="small" style={{ borderRadius: 12, marginBottom: 12 }} title="Alumnas por plan">
            <Table
              dataSource={planData}
              rowKey="name"
              size="small"
              pagination={false}
              columns={[
                { title: 'Plan', dataIndex: 'name', render: (v) => <Tag>{v}</Tag> },
                { title: 'Alumnas', dataIndex: 'count', align: 'right', width: 70 }
              ]}
              locale={{ emptyText: 'Sin datos' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ borderRadius: 12 }} title="Asistencias por clase">
        {classAttendance.length === 0 ? (
          <Text style={{ color: '#999' }}>Todavía no hay asistencias registradas</Text>
        ) : (
          <Table
            dataSource={classAttendance}
            rowKey="name"
            size="small"
            pagination={{ pageSize: 10, size: 'small' }}
            columns={[
              { title: 'Clase', dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
              { title: 'Asistencias', dataIndex: 'count', width: 110 }
            ]}
          />
        )}
      </Card>
    </div>
  )
}

export default Reportes
